import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, Grid, Card, CardContent,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  LinearProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Tooltip, Avatar,
} from '@mui/material';
import { AccessTime, Add, CheckCircle, Cancel, Close } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const STATUS_CFG: Record<string, { color: any; label: string }> = {
  pending:   { color: 'warning', label: 'Pending' },
  approved:  { color: 'success', label: 'Approved' },
  rejected:  { color: 'error',   label: 'Rejected' },
  cancelled: { color: 'default', label: 'Cancelled' },
};
const TYPE_CFG: Record<string, { label: string; rate: string }> = {
  weekday: { label: 'Weekday', rate: '1.5×' },
  weekend: { label: 'Weekend', rate: '2.0×' },
  holiday: { label: 'Holiday', rate: '2.0×' },
};
const COMP_CFG: Record<string, string> = {
  paid:     '💰 Paid',
  time_off: '⏰ TOIL',
};

function fmtMins(m: number | null) {
  if (!m) return '—';
  const h = Math.floor(m / 60), min = m % 60;
  return h ? `${h}h${min > 0 ? ` ${min}m` : ''}` : `${min}m`;
}
function calcMins(start: string, end: string) {
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  return (eh * 60 + em) - (sh * 60 + sm);
}

interface OTRequest { id: string; employeeId: string; date: string; startTime: string; endTime: string; durationMinutes: number; type: string; compensation: string; reason: string; workDone?: string; status: string; approverNote?: string; rateMultiplier: number; toilHours?: number; employee?: { firstName: string; lastName: string; employeeId: string }; approver?: { firstName: string; lastName: string }; approvedAt?: string; }

const emptyForm = (): Partial<OTRequest> => ({
  date: new Date().toISOString().slice(0, 10),
  startTime: '18:00', endTime: '21:00',
  type: 'weekday', compensation: 'paid', reason: '',
});

export default function Overtime() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager'].includes(user?.role ?? '');
  const isManager = isHR || user?.role === 'manager';

  const [tab, setTab] = useState(0);
  const [myOT, setMyOT] = useState<OTRequest[]>([]);
  const [pending, setPending] = useState<OTRequest[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState<Partial<OTRequest> | null>(null);
  const [approveDialog, setApproveDialog] = useState<{ req: OTRequest; action: 'approve' | 'reject'; note: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [my, st] = await Promise.all([
        apiFetch('/api/overtime/my'),
        apiFetch('/api/overtime/stats'),
      ]);
      setMyOT(my || []); setStats(st);
      if (isManager) {
        const p = await apiFetch('/api/overtime/pending');
        setPending(p || []);
      }
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, [isManager]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!dialog) return;
    try {
      const dur = calcMins(dialog.startTime!, dialog.endTime!);
      if (dur <= 0) { setError('End time must be after start'); return; }
      await apiFetch('/api/overtime', { method: 'POST', body: JSON.stringify({ ...dialog, durationMinutes: dur }) });
      setDialog(null); load();
    } catch (e: any) { setError(e.message); }
  };

  const doApproval = async () => {
    if (!approveDialog) return;
    try {
      const endpoint = approveDialog.action === 'approve'
        ? `/api/overtime/${approveDialog.req.id}/approve`
        : `/api/overtime/${approveDialog.req.id}/reject`;
      await apiFetch(endpoint, { method: 'PATCH', body: JSON.stringify({ note: approveDialog.note }) });
      setApproveDialog(null); load();
    } catch (e: any) { setError(e.message); }
  };

  const cancel = async (id: string) => {
    try { await apiFetch(`/api/overtime/${id}/cancel`, { method: 'PATCH' }); load(); }
    catch (e: any) { setError(e.message); }
  };

  const durMins = dialog?.startTime && dialog?.endTime ? calcMins(dialog.startTime, dialog.endTime) : 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccessTime sx={{ fontSize: 32, color: '#1a237e' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Overtime</Typography>
            <Typography variant="body2" color="text.secondary">Request and manage overtime hours</Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => setDialog(emptyForm())}
          sx={{ bgcolor: '#1a237e' }}>
          Request OT
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Stats */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card sx={{ textAlign: 'center', p: 1 }}>
              <CardContent sx={{ pb: '8px !important' }}>
                <Typography variant="h4" fontWeight={700} color="#1a237e">{stats.totalHours}</Typography>
                <Typography variant="caption" color="text.secondary">OT Hours YTD</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card sx={{ textAlign: 'center', p: 1 }}>
              <CardContent sx={{ pb: '8px !important' }}>
                <Typography variant="h4" fontWeight={700} color="#e65100">{stats.pendingCount}</Typography>
                <Typography variant="caption" color="text.secondary">Pending</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card sx={{ textAlign: 'center', p: 1 }}>
              <CardContent sx={{ pb: '8px !important' }}>
                <Typography variant="h4" fontWeight={700} color="#2e7d32">{stats.payHours}</Typography>
                <Typography variant="caption" color="text.secondary">Paid Hours</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card sx={{ textAlign: 'center', p: 1 }}>
              <CardContent sx={{ pb: '8px !important' }}>
                <Typography variant="h4" fontWeight={700} color="#4a148c">{stats.toilHours}</Typography>
                <Typography variant="caption" color="text.secondary">TOIL Hours</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="My Requests" />
          {isManager && <Tab label={`Pending Approval (${pending.length})`} />}
        </Tabs>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* ── MY REQUESTS ── */}
      {tab === 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell><b>Date</b></TableCell>
                <TableCell><b>Time</b></TableCell>
                <TableCell><b>Duration</b></TableCell>
                <TableCell><b>Type</b></TableCell>
                <TableCell><b>Compensation</b></TableCell>
                <TableCell><b>Reason</b></TableCell>
                <TableCell><b>Status</b></TableCell>
                <TableCell><b>Actions</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {myOT.map(r => (
                <TableRow key={r.id} hover>
                  <TableCell>{new Date(r.date + 'T00:00:00').toLocaleDateString('en-HK', { day: 'numeric', month: 'short' })}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.startTime?.slice(0,5)} – {r.endTime?.slice(0,5)}</TableCell>
                  <TableCell>{fmtMins(r.durationMinutes * 60)}</TableCell>
                  <TableCell>
                    <Chip label={TYPE_CFG[r.type]?.label + ' ' + TYPE_CFG[r.type]?.rate} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{COMP_CFG[r.compensation] ?? r.compensation}</TableCell>
                  <TableCell sx={{ maxWidth: 160 }}><Typography variant="body2" noWrap>{r.reason}</Typography></TableCell>
                  <TableCell>
                    <Chip label={STATUS_CFG[r.status]?.label ?? r.status} size="small" color={STATUS_CFG[r.status]?.color} />
                    {r.approverNote && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{r.approverNote}</Typography>}
                  </TableCell>
                  <TableCell>
                    {r.status === 'pending' && (
                      <Button size="small" color="error" onClick={() => cancel(r.id)}>Cancel</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {myOT.length === 0 && (
                <TableRow><TableCell colSpan={8} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No overtime requests yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── PENDING APPROVAL ── */}
      {tab === 1 && isManager && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell><b>Employee</b></TableCell>
                <TableCell><b>Date</b></TableCell>
                <TableCell><b>Time</b></TableCell>
                <TableCell><b>Hours</b></TableCell>
                <TableCell><b>Type</b></TableCell>
                <TableCell><b>Compensation</b></TableCell>
                <TableCell><b>Reason</b></TableCell>
                <TableCell><b>Actions</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pending.map(r => (
                <TableRow key={r.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 28, height: 28, bgcolor: '#1a237e', fontSize: '0.7rem' }}>
                        {r.employee?.firstName?.[0]}{r.employee?.lastName?.[0]}
                      </Avatar>
                      <Typography variant="body2">{r.employee?.firstName} {r.employee?.lastName}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{new Date(r.date + 'T00:00:00').toLocaleDateString('en-HK', { weekday: 'short', day: 'numeric', month: 'short' })}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.startTime?.slice(0,5)} – {r.endTime?.slice(0,5)}</TableCell>
                  <TableCell>{fmtMins(r.durationMinutes * 60)}</TableCell>
                  <TableCell><Chip label={TYPE_CFG[r.type]?.label} size="small" variant="outlined" /></TableCell>
                  <TableCell>{COMP_CFG[r.compensation] ?? r.compensation}</TableCell>
                  <TableCell sx={{ maxWidth: 160 }}><Typography variant="body2" noWrap>{r.reason}</Typography></TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Approve">
                        <IconButton size="small" color="success" onClick={() => setApproveDialog({ req: r, action: 'approve', note: '' })}>
                          <CheckCircle fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reject">
                        <IconButton size="small" color="error" onClick={() => setApproveDialog({ req: r, action: 'reject', note: '' })}>
                          <Cancel fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {pending.length === 0 && (
                <TableRow><TableCell colSpan={8} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No pending requests. All caught up! ✅</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── REQUEST OT DIALOG ── */}
      <Dialog open={!!dialog} onClose={() => setDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Request Overtime
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setDialog(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Date" type="date" fullWidth InputLabelProps={{ shrink: true }}
              value={dialog?.date ?? ''} onChange={e => setDialog(p => ({ ...p, date: e.target.value }))} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Start Time" type="time" fullWidth InputLabelProps={{ shrink: true }}
                value={dialog?.startTime ?? '18:00'} onChange={e => setDialog(p => ({ ...p, startTime: e.target.value }))} />
              <TextField label="End Time" type="time" fullWidth InputLabelProps={{ shrink: true }}
                value={dialog?.endTime ?? '21:00'} onChange={e => setDialog(p => ({ ...p, endTime: e.target.value }))} />
            </Box>
            {durMins > 0 && (
              <Typography variant="body2" color="primary" fontWeight={600}>
                Duration: {fmtMins(durMins)} · Rate: {TYPE_CFG[dialog?.type ?? 'weekday']?.rate}
              </Typography>
            )}
            <TextField label="OT Type" select fullWidth value={dialog?.type ?? 'weekday'}
              onChange={e => setDialog(p => ({ ...p, type: e.target.value }))}>
              <MenuItem value="weekday">Weekday (1.5×)</MenuItem>
              <MenuItem value="weekend">Weekend (2.0×)</MenuItem>
              <MenuItem value="holiday">Holiday (2.0×)</MenuItem>
            </TextField>
            <TextField label="Compensation" select fullWidth value={dialog?.compensation ?? 'paid'}
              onChange={e => setDialog(p => ({ ...p, compensation: e.target.value }))}>
              <MenuItem value="paid">💰 Paid OT</MenuItem>
              <MenuItem value="time_off">⏰ Time Off In Lieu (TOIL)</MenuItem>
            </TextField>
            <TextField label="Reason / Project" fullWidth required value={dialog?.reason ?? ''}
              onChange={e => setDialog(p => ({ ...p, reason: e.target.value }))} />
            <TextField label="Work Description (optional)" fullWidth multiline rows={2} value={dialog?.workDone ?? ''}
              onChange={e => setDialog(p => ({ ...p, workDone: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="contained" sx={{ bgcolor: '#1a237e' }} onClick={submit}
            disabled={!dialog?.date || !dialog?.reason || durMins <= 0}>
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── APPROVE/REJECT DIALOG ── */}
      <Dialog open={!!approveDialog} onClose={() => setApproveDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{approveDialog?.action === 'approve' ? '✅ Approve' : '❌ Reject'} OT Request</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography>
              <b>{approveDialog?.req.employee?.firstName} {approveDialog?.req.employee?.lastName}</b><br />
              {approveDialog?.req.date} · {approveDialog?.req.startTime?.slice(0,5)}–{approveDialog?.req.endTime?.slice(0,5)} ·{' '}
              {fmtMins((approveDialog?.req.durationMinutes ?? 0) * 60)}
            </Typography>
            <TextField label="Note (optional)" fullWidth multiline rows={2}
              value={approveDialog?.note ?? ''}
              onChange={e => setApproveDialog(p => p ? { ...p, note: e.target.value } : null)} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialog(null)}>Back</Button>
          <Button
            variant="contained"
            color={approveDialog?.action === 'approve' ? 'success' : 'error'}
            onClick={doApproval}
          >
            {approveDialog?.action === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
